import React, { useState } from 'react';
import { AlertCircle, Trash2, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { IndustryType } from '../types';
import { Navbar } from '../components/common/Navbar';
import { Sidebar } from '../components/common/Sidebar';
import { MobileBottomNav } from '../components/common/MobileBottomNav';

// Views
import { DashboardOverview } from '../components/dashboard/DashboardOverview';
import { SitesList } from '../components/dashboard/SitesList';
import { BusinessProfileManager } from '../components/dashboard/BusinessProfileManager';
import { ServicesManager } from '../components/dashboard/ServicesManager';
import { DesignSystemManager } from '../components/dashboard/DesignSystemManager';
import { TestimonialsManager } from '../components/dashboard/TestimonialsManager';
import { LocalSeoManager } from '../components/seo/LocalSeoManager';
import { EditorHeader } from '../components/editor/EditorHeader';
import { EditorCanvas } from '../components/editor/EditorCanvas';
import { ComponentPalette } from '../components/editor/ComponentPalette';
import { PropertiesPanel } from '../components/editor/PropertiesPanel';
import { MobilePropertySheet } from '../components/editor/MobilePropertySheet';
import { MobilePageSheet } from '../components/editor/MobilePageSheet';
import { MobileEditorBottomBar } from '../components/editor/MobileEditorBottomBar';
import { AddSectionModal } from '../components/editor/AddSectionModal';
import { PageManagerModal } from '../components/editor/PageManagerModal';

// Sector & Management Modules
import { YachtList } from '../components/yachts/YachtList';
import { RouteList } from '../components/yachts/RouteList';
import { MarinaList } from '../components/yachts/MarinaList';
import { ReservationList } from '../components/yachts/ReservationList';
import { ReservationCalendar } from '../components/yachts/ReservationCalendar';
import { CommunicationsManager } from '../components/communications/CommunicationsManager';
import { BlogManager } from '../components/blog/BlogManager';
import { DomainManager } from '../components/domains/DomainManager';
import { MediaManager } from '../components/media/MediaManager';
import { SeoManager } from '../components/seo/SeoManager';
import { SubscriptionPlans } from '../components/billing/SubscriptionPlans';
import { CustomerSubscriptionsView } from '../components/billing/CustomerSubscriptionsView';
import { AIVisibilityView } from '../components/ai-visibility/AIVisibilityView';
import { SiteStatusView } from '../components/dashboard/SiteStatusView';
import { AccountSettingsView } from '../components/settings/AccountSettingsView';

// Modals
import { LiveSiteRenderer } from '../components/live/LiveSiteRenderer';
import { SiteCreationWizard } from '../components/onboarding/SiteCreationWizard';
import { AISiteGeneratorWizard } from '../components/ai/AISiteGeneratorWizard';
import { ShopierModal } from '../components/billing/ShopierModal';
import { SiteDuplicateModal } from '../components/dashboard/SiteDuplicateModal';
import { VersionHistoryModal } from '../components/dashboard/VersionHistoryModal';
import { SectorSelectionModal } from '../components/onboarding/SectorSelectionModal';
import { ToastContainer } from '../components/common/ToastContainer';
import { CustomerThemeProvider, EditorThemeProvider } from '../theme';

interface WorkspaceAppProps {
  onReturnToMarketing?: () => void;
}

export const WorkspaceApp: React.FC<WorkspaceAppProps> = ({ onReturnToMarketing }) => {
  const { activeSite, sites, setActiveSiteId, selectedSectionId, sectorQuotas } = useSite();
  const { currentUser } = useAuth();

  const activeNonDeletedSites = sites.filter((site) => !site.isDeleted && site.status !== 'DELETED');
  const hasActiveWebsite = activeNonDeletedSites.length >= 1;
  const hasWebsiteSubscription = sectorQuotas?.CORPORATE?.allowed || sectorQuotas?.YACHT_RENTAL?.allowed;

  const [activeSiteExistsModalOpen, setActiveSiteExistsModalOpen] = useState(false);

  const handleOpenCreationFlow = (mode: 'ai' | 'standard' = 'ai') => {
    if (!hasWebsiteSubscription) {
      setCurrentView('billing');
      return;
    }
    if (hasActiveWebsite) {
      setActiveSiteExistsModalOpen(true);
      return;
    }
    // If only Corporate is active:
    if (sectorQuotas?.CORPORATE?.allowed && !sectorQuotas?.YACHT_RENTAL?.allowed) {
      if (mode === 'ai') {
        setAiWizardIndustry('CORPORATE');
        setIsAiWizardOpen(true);
      } else {
        setWizardIndustry('CORPORATE');
        setIsWizardOpen(true);
      }
      return;
    }
    // If only Yacht is active:
    if (sectorQuotas?.YACHT_RENTAL?.allowed && !sectorQuotas?.CORPORATE?.allowed) {
      if (mode === 'ai') {
        setAiWizardIndustry('YACHT_RENTAL');
        setIsAiWizardOpen(true);
      } else {
        setWizardIndustry('YACHT_RENTAL');
        setIsWizardOpen(true);
      }
      return;
    }
    setIsSectorModalOpen(true);
  };

  // Navigation State
  const [currentView, setCurrentView] = useState<string>('dashboard');
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [insertSectionIndex, setInsertSectionIndex] = useState<number | undefined>(undefined);
  const [isPageManagerModalOpen, setIsPageManagerModalOpen] = useState(false);
  const [isMobilePageSheetOpen, setIsMobilePageSheetOpen] = useState(false);
  const [isLivePreviewOpen, setIsLivePreviewOpen] = useState(false);
  const [isSectorModalOpen, setIsSectorModalOpen] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [wizardIndustry, setWizardIndustry] = useState<IndustryType | undefined>(undefined);
  const [isAiWizardOpen, setIsAiWizardOpen] = useState(false);
  const [aiWizardIndustry, setAiWizardIndustry] = useState<IndustryType | undefined>(undefined);
  const [isShopierModalOpen, setIsShopierModalOpen] = useState(false);
  const [isMobilePropertiesOpen, setIsMobilePropertiesOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isVersionHistoryModalOpen, setIsVersionHistoryModalOpen] = useState(false);
  const [isPropertiesPanelOpen, setIsPropertiesPanelOpen] = useState(true);

  // Dedicated Editor Shell Isolation (renders completely outside CustomerThemeProvider)
  if (currentView === 'editor') {
    return (
      <EditorThemeProvider className="h-screen flex flex-col bg-[#020617] overflow-hidden relative">
        <ToastContainer />
        <EditorHeader
          onOpenNewPageModal={() => setIsPageManagerModalOpen(true)}
          onOpenLiveSite={() => setIsLivePreviewOpen(true)}
          onOpenPageSheet={() => setIsMobilePageSheetOpen(true)}
          onBackToDashboard={() => setCurrentView('dashboard')}
          onOpenAddSectionModal={() => {
            setInsertSectionIndex(undefined);
            setIsAddSectionModalOpen(true);
          }}
          onToggleProperties={() => setIsPropertiesPanelOpen(!isPropertiesPanelOpen)}
          isPropertiesOpen={isPropertiesPanelOpen}
        />

        <div className="flex-1 flex overflow-hidden relative pb-14 lg:pb-0">
          {/* Desktop Component Palette */}
          <div className="hidden lg:block">
            <ComponentPalette />
          </div>

          {/* Central Canvas */}
          <EditorCanvas
            onOpenAddSectionModal={(idx) => {
              setInsertSectionIndex(idx);
              setIsAddSectionModalOpen(true);
            }}
            onOpenProperties={() => {
              setIsPropertiesPanelOpen(true);
              setIsMobilePropertiesOpen(true);
            }}
          />

          {/* Desktop Properties Panel */}
          {isPropertiesPanelOpen && (
            <div className="hidden lg:block">
              <PropertiesPanel />
            </div>
          )}

          {/* Dedicated Mobile Editor Bottom Action Bar */}
          <MobileEditorBottomBar
            onOpenAddSection={() => {
              setInsertSectionIndex(undefined);
              setIsAddSectionModalOpen(true);
            }}
            onOpenAiGenerator={() => setIsAiWizardOpen(true)}
            onOpenPageSheet={() => setIsMobilePageSheetOpen(true)}
            onOpenSettings={() => setIsMobilePropertiesOpen(true)}
          />

          {/* Mobile Bottom Property Sheet */}
          <MobilePropertySheet
            isOpen={isMobilePropertiesOpen}
            onClose={() => setIsMobilePropertiesOpen(false)}
          />

          {/* Mobile Page Switcher Sheet */}
          <MobilePageSheet
            isOpen={isMobilePageSheetOpen}
            onClose={() => setIsMobilePageSheetOpen(false)}
            onOpenNewPageModal={() => setIsPageManagerModalOpen(true)}
          />
        </div>

        {/* Editor Modals */}
        <AddSectionModal
          isOpen={isAddSectionModalOpen}
          onClose={() => setIsAddSectionModalOpen(false)}
          insertIndex={insertSectionIndex}
        />

        <PageManagerModal
          isOpen={isPageManagerModalOpen}
          onClose={() => setIsPageManagerModalOpen(false)}
        />

        {isLivePreviewOpen && (
          <LiveSiteRenderer onClose={() => setIsLivePreviewOpen(false)} />
        )}

        <SiteDuplicateModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
        />

        <VersionHistoryModal
          isOpen={isVersionHistoryModalOpen}
          onClose={() => setIsVersionHistoryModalOpen(false)}
        />

        <AISiteGeneratorWizard
          isOpen={isAiWizardOpen}
          initialIndustry={aiWizardIndustry}
          onClose={() => {
            setIsAiWizardOpen(false);
            setAiWizardIndustry(undefined);
          }}
          onSiteCreated={(siteId) => {
            setActiveSiteId(siteId);
            setCurrentView('editor');
          }}
        />
      </EditorThemeProvider>
    );
  }

  return (
    <CustomerThemeProvider className="min-h-screen bg-slate-950 flex flex-col font-sans text-slate-100 antialiased selection:bg-blue-600 selection:text-white">
      {/* Toast Notification Layer */}
      <ToastContainer />

      {/* Top Multi-Tenant Navbar */}
      <Navbar
        currentTab={currentView}
        onSelectTab={(tab) => setCurrentView(tab)}
        onOpenWizard={() => handleOpenCreationFlow('standard')}
        onOpenAiWizard={() => handleOpenCreationFlow('ai')}
        onOpenLivePreview={() => setIsLivePreviewOpen(true)}
        onOpenBilling={() => setCurrentView('billing')}
        onReturnToMarketing={onReturnToMarketing}
        onToggleMobileMenu={() => setIsMobileDrawerOpen(!isMobileDrawerOpen)}
        isMobileMenuOpen={isMobileDrawerOpen}
      />

      {/* Main SaaS Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Left Global Sidebar (Desktop persistent + Mobile Drawer) */}
        <Sidebar
          currentView={currentView}
          currentTab={currentView}
          onSelectTab={(tab) => setCurrentView(tab)}
          onViewChange={(view) => setCurrentView(view)}
          onOpenLiveSite={() => setIsLivePreviewOpen(true)}
          onOpenWizard={() => handleOpenCreationFlow('standard')}
          isMobileOpen={isMobileDrawerOpen}
          onCloseMobile={() => setIsMobileDrawerOpen(false)}
        />

        {/* Dynamic Center Area */}
        <main className="flex-1 overflow-y-auto min-w-0 pb-20 lg:pb-0 bg-slate-100 text-slate-900">
          {/* 0. SITES LIST (ALL PROJECTS) */}
          {currentView === 'sites' && (
            <SitesList
              onOpenWizard={(ind) => {
                setWizardIndustry(ind);
                setIsWizardOpen(true);
              }}
              onOpenAiWizard={(ind) => {
                setAiWizardIndustry(ind);
                setIsAiWizardOpen(true);
              }}
              onOpenEditor={(siteId) => {
                setActiveSiteId(siteId);
                setCurrentView('editor');
              }}
              onOpenLiveSite={(site) => {
                setActiveSiteId(site.id);
                setIsLivePreviewOpen(true);
              }}
              onSelectSite={(siteId) => {
                setActiveSiteId(siteId);
                setCurrentView('dashboard');
              }}
              onNavigate={(view) => setCurrentView(view)}
            />
          )}

          {/* 1. DASHBOARD */}
          {currentView === 'dashboard' && (
            <DashboardOverview
              onOpenEditor={() => setCurrentView('editor')}
              onOpenLivePreview={() => setIsLivePreviewOpen(true)}
              onNavigate={(view) => setCurrentView(view)}
              onOpenCreateSite={() => setIsSectorModalOpen(true)}
            />
          )}

          {/* 1.1 SITE DURUMU (OPERATIONAL HEALTH & DOMAIN/SSL) */}
          {currentView === 'site-status' && (
            <SiteStatusView
              onOpenEditor={() => setCurrentView('editor')}
              onOpenLivePreview={() => setIsLivePreviewOpen(true)}
              onNavigate={(view) => setCurrentView(view)}
            />
          )}

          {/* 3. CORPORATE CENTRAL BUSINESS PROFILE */}
          {currentView === 'business-profile' && <BusinessProfileManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 4. CORPORATE SERVICES MANAGER */}
          {currentView === 'services' && <ServicesManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 5. DESIGN SYSTEM & COMPONENT VARIATIONS */}
          {(currentView === 'design-system' || currentView === 'design') && <DesignSystemManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 6. TESTIMONIALS & REFERENCES */}
          {currentView === 'testimonials' && <TestimonialsManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 7. LOCAL SEO & SCHEMA */}
          {currentView === 'local-seo' && <LocalSeoManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 8. YACHT FLEET MANAGEMENT */}
          {currentView === 'yachts' && <YachtList onNavigate={(view) => setCurrentView(view)} />}

          {/* 8.1 YACHT BLUE CRUISE ROUTES */}
          {currentView === 'routes' && <RouteList onNavigate={(view) => setCurrentView(view)} />}

          {/* 8.2 YACHT MARINAS & PORTS */}
          {currentView === 'marinas' && <MarinaList onNavigate={(view) => setCurrentView(view)} />}

          {/* 9. RESERVATION REQUESTS */}
          {currentView === 'reservations' && <ReservationList onNavigate={(view) => setCurrentView(view)} />}

          {/* 10. AVAILABILITY CALENDAR */}
          {currentView === 'calendar' && <ReservationCalendar onNavigate={(view) => setCurrentView(view)} />}

          {/* 11. COMMUNICATIONS / CONTACT INBOX */}
          {currentView === 'communications' && <CommunicationsManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 12. BLOG & CONTENT MARKETING */}
          {currentView === 'blog' && <BlogManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 13. CUSTOM DOMAIN & SSL */}
          {currentView === 'domains' && <DomainManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 14. MEDIA ASSET LIBRARY */}
          {currentView === 'media' && <MediaManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 15. SEO & SITEMAP GENERATOR */}
          {currentView === 'seo' && <SeoManager onNavigate={(view) => setCurrentView(view)} />}

          {/* 15.1 AI ARAMA GÖRÜNÜRLÜĞÜ */}
          {currentView === 'ai-visibility' && <AIVisibilityView onNavigate={(view) => setCurrentView(view)} />}

          {/* 16. BILLING & SHOPIER PACKAGES & SUBSCRIPTIONS */}
          {currentView === 'billing' && <SubscriptionPlans />}
          {currentView === 'subscriptions' && <CustomerSubscriptionsView onNavigate={(view) => setCurrentView(view)} />}

          {/* 17. ACCOUNT & SYSTEM SETTINGS */}
          {currentView === 'settings' && <AccountSettingsView />}
        </main>
      </div>

      {/* Mobile Bottom Navigation Bar (Hidden when in Editor mode to give priority to Editor tools) */}
      {currentView !== 'editor' && (
        <MobileBottomNav
          currentView={currentView}
          onNavigate={(view) => setCurrentView(view)}
          onOpenMenu={() => setIsMobileDrawerOpen(true)}
        />
      )}

      {/* MODALS */}
      <AddSectionModal
        isOpen={isAddSectionModalOpen}
        onClose={() => setIsAddSectionModalOpen(false)}
        insertIndex={insertSectionIndex}
      />

      <PageManagerModal
        isOpen={isPageManagerModalOpen}
        onClose={() => setIsPageManagerModalOpen(false)}
      />

      {isLivePreviewOpen && (
        <LiveSiteRenderer onClose={() => setIsLivePreviewOpen(false)} />
      )}

      {/* Sector Selection & Quota Modal */}
      <SectorSelectionModal
        isOpen={isSectorModalOpen}
        onClose={() => setIsSectorModalOpen(false)}
        onSelectSector={(sector, mode) => {
          setIsSectorModalOpen(false);
          if (mode === 'ai') {
            setAiWizardIndustry(sector);
            setIsAiWizardOpen(true);
          } else {
            setWizardIndustry(sector);
            setIsWizardOpen(true);
          }
        }}
        onOpenBilling={() => {
          setIsSectorModalOpen(false);
          setCurrentView('billing');
        }}
      />

      <SiteCreationWizard
        isOpen={isWizardOpen}
        initialIndustry={wizardIndustry}
        onClose={() => {
          setIsWizardOpen(false);
          setWizardIndustry(undefined);
        }}
        onOpenAiWizard={(ind) => {
          setAiWizardIndustry(ind || wizardIndustry || 'CORPORATE');
          setIsAiWizardOpen(true);
        }}
        onSiteCreated={(siteId) => {
          setActiveSiteId(siteId);
          setCurrentView('editor');
        }}
      />

      <AISiteGeneratorWizard
        isOpen={isAiWizardOpen}
        initialIndustry={aiWizardIndustry}
        onClose={() => {
          setIsAiWizardOpen(false);
          setAiWizardIndustry(undefined);
        }}
        onSiteCreated={(siteId) => {
          setActiveSiteId(siteId);
          setCurrentView('editor');
        }}
      />

      {isShopierModalOpen && (
        <ShopierModal
          isOpen={isShopierModalOpen}
          onClose={() => setIsShopierModalOpen(false)}
        />
      )}

      <SiteDuplicateModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
      />

      <VersionHistoryModal
        isOpen={isVersionHistoryModalOpen}
        onClose={() => setIsVersionHistoryModalOpen(false)}
      />

      {/* Already Has Active Website Modal (Requirement 14) */}
      {activeSiteExistsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 text-slate-900">
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto border border-amber-200">
              <AlertCircle className="w-6 h-6" />
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold text-slate-900">Bu hesapta zaten aktif bir website bulunmaktadır.</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Adiosoft sisteminde 1 hesap = 1 aktif website paketi = 1 aktif website kuralı kesin olarak geçerlidir. Aynı hesapta iki web sitesi aynı anda aktif olamaz.
              </p>
              <p className="text-xs text-slate-500">
                Mevcut sitenizi yönetebilir veya yeni bir site kurmak için mevcut sitenizi tamamen silebilirsiniz. Aynı anda iki farklı site yayınlamak istiyorsanız lütfen yeni bir Adiosoft hesabı oluşturunuz.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-2.5 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setActiveSiteExistsModalOpen(false)}
                className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition"
              >
                Kapat
              </button>
              {activeNonDeletedSites.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSiteExistsModalOpen(false);
                      setCurrentView('sites');
                    }}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold transition flex items-center justify-center gap-1.5 border border-rose-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Website'i Sil</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSiteExistsModalOpen(false);
                      setActiveSiteId(activeNonDeletedSites[0].id);
                      setCurrentView('editor');
                    }}
                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-md shadow-blue-500/25"
                  >
                    <span>Mevcut Website'i Yönet</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </CustomerThemeProvider>
  );
};

export default WorkspaceApp;
